import {all, create} from "mathjs"

const config = {
    matrix: 'Array',
}

// import the function into math.js. Raw functions must be imported in the
// math namespace, they can't be used via `evaluate(scope)`.
export const math = create(all, config)

// You can adjust this for more accuracy, trapezium rule steps
export const n_rep = 7_500_000

export class WorkerState {
    acoustic_power = null
    nPulses = null
    pulseDur = null
    pulseRepetitionInterval = null
    pulseTrainDuration = null
    pulseTrainRampDuration = null
    pulseTrainRampShape = null
    pulseTrainRepetitionInterval = null
    pulseTrainRepetitionDuration = null
    pulseTrainRepetitionRampShape = null
    pulseTrainRepetitionRampDuration = null
    nTrains = null
    pulse_ramp_shape = null
    pulse_ramp_duration_ms = null
    pulseTrainRepetitionIntervalWidth = null
    pulseRepetitionIntervalWidth = null
    pulseOnly = null
    pulseAndTrainOnly = null
    allTimeTogglesOn = null
    fund_freq = null
    ppp_mpa = null
    derated_ppp_mpa = null
    z_Rayl_FF = null
    z_Rayl_IS = null
    linkColor = "rgb(238,227,227, 50)"
    canvas = null
    padding = 5
    arbitrary_default_amplitude = null
    amplitude = null
    sampleRate = null
    pulseSampleRate = null
    canvas_width = null
    canvas_height = null
    showPulseTrain = false
    showPulseTrainRepetition = false
    ctx = null
    axisColor = 'black'
    textColor = 'black'
    pulseFillStyle = 'red'
    backgroundColor = 'white'
    isDarkModeGlobal = false
    canvasBlock = null
    isppaOld = 19.26

    pulseTrainSampleRate = null
    pulseTrainRepetitionSampleRate = null

    constructor(data) {
        this.acoustic_power = data.acoustic_power
        this.pulseOnly = data.pulseOnly
        this.pulseAndTrainOnly = data.pulseAndTrainOnly
        this.allTimeTogglesOn = data.allTimeTogglesOn
        this.ppp_mpa = data.ppp_mpa ? data.ppp_mpa : null
        this.derated_ppp_mpa = data.derated_ppp_mpa
        this.fund_freq = data.frequency_MHz * 10 ** 6
        this.z_Rayl_FF = data.z_Rayl_FF
        this.z_Rayl_IS = data.z_Rayl_IS
        this.pulseDur = data.pulseDur
        this.pulseRepetitionInterval = data.pulseRepetitionInterval
        this.pulseTrainDuration = data.pulseTrainDuration
        this.pulseTrainRampShape = data.pulseTrainRampShape
        this.pulseTrainRampDuration = data.pulseTrainRampDuration
        this.pulseTrainRepetitionInterval = data.pulseTrainRepetitionInterval
        this.pulseTrainRepetitionDuration = data.pulseTrainRepetitionDuration
        this.pulseTrainRepetitionRampShape = data.pulseTrainRepetitionRampShape
        this.pulseTrainRepetitionRampDuration = data.pulseTrainRepetitionRampDuration
        this.nPulses = data.nPulses
        this.nTrains = data.nTrains
        this.pulse_ramp_shape = data.pulse_ramp_shape
        this.pulse_ramp_duration_ms = data.pulse_ramp_duration_ms

        // used in calc newPampl
        this.isppa = data.isppa
        this.derated_isppa = data.derated_isppa
        this.isppaOld = data.isppaOld
        this.derated_isppaOld = data.derated_isppaOld

        this.arbitrary_default_amplitude = 100
        this.amplitude = 79

        this.sampleRate = data.sampleRate
        this.canvas_width = data.canvas_width || 1000
        this.canvas_height = data.canvas_height || 1000

        this.showPulseTrain = data.showPulseTrain
        this.showPulseTrainRepetition = data.showPulseTrainRepetition
        this.isDarkModeGlobal = data.isDarkModeGlobal

        if (this.isDarkModeGlobal) {
          this.axisColor = 'white'
          this.textColor = 'white'
          this.backgroundColor = '#1d1d1d'
          this.linkColor = "rgb(70,56,56)"
        } else {
          this.axisColor = 'black'
          this.textColor = 'black'
          this.backgroundColor = 'white'
          this.linkColor = "rgb(238,227,227, 50)"
        }

        this.pulseSampleRate = this.sampleRate
        this.pulseTrainSampleRate = this.sampleRate
        this.pulseTrainRepetitionSampleRate = this.sampleRate

        this.pulseTrainRepetitionIntervalWidth = this.canvas_width / this.nTrains
        this.pulseRepetitionIntervalWidth = this.canvas_width / this.nPulses
        this.canvasBlock = this.canvas_height / 10

        this.pampl = this.ppp_mpa * 10 ** 6
        this.derated_pampl = this.derated_ppp_mpa * 10 ** 6 // pressure amplitude in MPa

        this.PD = this.pulseDur / 1000 // pulse duration in seconds
        this.PTD = this.pulseTrainDuration / 1000 // pulse duration in seconds
        this.PTRD = this.pulseTrainRepetitionDuration / 1000 // pulse duration in seconds

        this.Z_FF = this.z_Rayl_FF * 10 ** 6 // acoustic impedance
        this.Z_IS = this.z_Rayl_IS * 10 ** 6 // acoustic impedance
    }

    getSignal(shapeWidth, pulsePressureSignalOff) {
        let pulseResult,
            pulsePressureSignalMaxAmpl,
            pulsePressureSignalRampUp,
            pulsePressureSignalRampDown

        if (this.pulse_ramp_duration_ms === 0) {
            pulseResult = math.concat(
                math.ones(Math.ceil(shapeWidth)),
                pulsePressureSignalOff
            )
        } else {
            const alphaOfPulse = (1 / this.pulseDur) * (this.pulse_ramp_duration_ms * 2)
            const rampWidth = shapeWidth * (alphaOfPulse / 2)
            pulsePressureSignalRampUp = calculateRamping(rampWidth, this.pulse_ramp_shape)
            pulsePressureSignalRampDown = calculateRamping(rampWidth, this.pulse_ramp_shape, '-')
            pulsePressureSignalMaxAmpl = math.ones(Math.floor(shapeWidth - (shapeWidth * alphaOfPulse)))
            pulseResult = math.concat(pulsePressureSignalRampUp,
                pulsePressureSignalMaxAmpl,
                pulsePressureSignalRampDown,
                pulsePressureSignalOff
            )
        }
        return pulseResult
    }

    getPulseSignal(shapeWidth) {
        let pulseResult,
            pulsePressureSignalMaxAmpl,
            pulsePressureSignalRampUp,
            pulsePressureSignalRampDown

        if (this.pulse_ramp_duration_ms === 0) {
            pulseResult = math.ones(Math.ceil(shapeWidth))
        } else {
            const alphaOfPulse = (1 / this.pulseDur) * (this.pulse_ramp_duration_ms * 2)
            const rampWidth = shapeWidth * (alphaOfPulse / 2)
            pulsePressureSignalRampUp = calculateRamping(rampWidth, this.pulse_ramp_shape)
            pulsePressureSignalRampDown = calculateRamping(rampWidth, this.pulse_ramp_shape, '-')
            pulsePressureSignalMaxAmpl = math.ones(Math.floor(shapeWidth - (shapeWidth * alphaOfPulse)))
            pulseResult = math.concat(pulsePressureSignalRampUp,
                pulsePressureSignalMaxAmpl,
                pulsePressureSignalRampDown
            )
        }
        return pulseResult
    }

    getISSPAParameters () {
        let R1s = 0 // start time of pulse ramping up in seconds
        let R1e = this.pulse_ramp_duration_ms / 1000 // end time of pulse ramping up in seconds
        let R2s = (this.pulseDur - this.pulse_ramp_duration_ms) / 1000 // start time of pulse ramping down in seconds
        let R2e = this.pulseDur / 1000 // end time of pulse ramping down in seconds
        let offTime = (this.pulseRepetitionInterval - this.pulseDur) / 1000
        return [ R1s, R1e, R2s, R2e, offTime ]
    }


    getISPTAParameters () {
        const R1s = 0;

        // Calculate ramp fraction (same as pulse logic)
        const rampFraction = Math.min(1, (this.pulseTrainRampDuration / this.pulseTrainDuration));
        const trainDurationSec = this.pulseTrainDuration / 1000;

        const R1e = rampFraction * trainDurationSec;
        const R2s = trainDurationSec - (rampFraction * trainDurationSec);
        const R2e = trainDurationSec;

        const offTime = (this.pulseTrainRepetitionInterval - this.pulseTrainDuration) / 1000;
        const period = this.pulseTrainRepetitionInterval / 1000;

        return [ R1s, R1e, R2s, R2e, offTime, period ];
    }

    getISPTAPTRParameters() {
        const R1s = 0; // start time of PTR ramp-up in seconds

        // Calculate ramp fraction (same as pulse and train logic)
        const rampFraction = Math.min(1, (this.pulseTrainRepetitionRampDuration / this.pulseTrainRepetitionDuration));
        const ptrDurationSec = this.pulseTrainRepetitionDuration / 1000;

        const R1e = rampFraction * ptrDurationSec;
        const R2s = ptrDurationSec - (rampFraction * ptrDurationSec);
        const R2e = ptrDurationSec;

        return [R1s, R1e, R2s, R2e];
    }

    }


export function calculateRamping(points, rampShape, orientation) {
  if (rampShape === RAMPSHAPES.Linear) {
      return customLinSpace(
          0.0,
          1.0,
          1.0 / Math.floor(points),
          orientation)
  } else if (rampShape === RAMPSHAPES.Tukey) {
      return math.map(
          customLinSpace(
              0.0,
              0.5,
              0.5 / points,
              orientation),
          (x) => 0.5 * (1 + Math.cos((2 * Math.PI) * (x - 0.5)))
              .toFixed(6)
      )
  }
  return []
}

function customLinSpace(start, end, points, orientation) {
    return orientation === '-'
        ? math.range(end, start, -1 * points)
        : math.range(start, end, points)
}

export const GRAPHS = {
  Train: "train",
  Single: "pulse",
  Repetition: "repetition"
}

export const RAMPSHAPES = {
  Tukey: "Tukey",
  Linear: "Linear"
}

export function tukeyRampingAmplitude(t, R1s, R1e, R2s, R2e, alpha = 0.5) {
    const total = R2e - R1s;
    if (total <= 0) return 0;

    // Normalize time to [0, 1]
    const rel = (t - R1s) / total;
    if (rel < 0 || rel > 1) return 0;

    // Tukey window formula (α fraction of cosine taper)
    if (rel < alpha / 2) {
        // Rising cosine ramp
        return 0.5 * (1 + Math.cos(Math.PI * ((2 * rel / alpha) - 1)));
    } else if (rel <= 1 - alpha / 2) {
        // Flat region
        return 1;
    } else {
        // Falling cosine ramp
        return 0.5 * (1 + Math.cos(Math.PI * ((2 * rel / alpha) - (2 / alpha) + 1)));
    }
}


export function linearRampingAmplitude(t, R1s, R1e, R2s, R2e) {
    if (R1s <= t && t < R1e) {
        if (Math.abs(R1e - R1s) <= 1e-09) {
            return 1
        } else {
            return (t - R1s) / (R1e - R1s)
        }
    } else if (R1e <= t && t <= R2s) {
        return 1
    } else if (R2s < t && t <= R2e) {
        if (Math.abs(R2e - R2s) <= 1e-09) {
            return 1
        } else {
            return (R2e - t) / (R2e - R2s)
        }
    } else {
        return 0
    }
}

// Xorshift32 PRNG (fast and deterministic)
function xorshift32(seed) {
    let state = seed;
    return function() {
        state ^= state << 13;
        state ^= state >>> 17;
        state ^= state << 5;
        return (state >>> 0) / 4294967296;
    };
}

const T_SMALL_SEC = 5

export function getNumberOfSamples(T) {
    if (T < T_SMALL_SEC) return n_rep
    // For T >= T_SMALL_SEC, scale samples proportionally to maintain accuracy
    // Using a square root relationship for scaling (N ∝ T^0.5) as a good balance
    // This provides more samples for larger T while avoiding excessive computation
    const scaleFactor = Math.sqrt(T / T_SMALL_SEC);
    // Cap the maximum number of samples to avoid excessive computation
    const maxScale = 20; // Maximum 20x the base samples
    return Math.min(Math.round(n_rep * scaleFactor), n_rep * maxScale);
}

export function monteCarloIntegration(a, b, numSamples, acousticalPower, entity, integrand, seed = 123456789) {
    let sum = 0;
    const range = b - a;
    for (let i = 0; i < numSamples; i++) {
        const random = xorshift32(seed + i); // Vary the seed slightly each iteration
        const t = random() * range + a; // Deterministic pseudo-random point in [a, b]
        sum += integrand(t, acousticalPower, entity);
    }
    return (range / numSamples) * sum; // Average value * range
}

export function getTIC(avg_acoustic_power, us_beam_diameter_on_skull_cm) {
    return +(+avg_acoustic_power / us_beam_diameter_on_skull_cm / 40).toFixed(3)
}